import React, { useState } from 'react';
import { api } from '@/api/client';
import SecretInput from './ui/SecretInput';
import type { User } from '@/types';

const PASSWORD_HINT = 'At least 8 characters with one letter and one number.';

type Props = {
  user: User;
  onClose: () => void;
  onSaved: (message: string) => void;
};

export default function ResetPasswordTeamMemberModal({ user, onClose, onSaved }: Props) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [error, setError] = useState('');

  async function resetPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!newPassword || newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setResettingPassword(true);
    setError('');
    try {
      await api.users.resetPassword(user.id, newPassword);
      onSaved('Password updated successfully');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password reset failed');
    } finally {
      setResettingPassword(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div className="modal card card-elevated" onClick={(e) => e.stopPropagation()} role="dialog">
        <h2>Reset password</h2>
        <p className="muted modal-hint">{user.email || user.name || user.id}</p>
        <p className="muted modal-hint">{PASSWORD_HINT}</p>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={resetPassword}>
          <label htmlFor="reset-team-password">
            New password
            <SecretInput
              id="reset-team-password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              required
            />
          </label>
          <label htmlFor="reset-team-password-confirm">
            Confirm password
            <SecretInput
              id="reset-team-password-confirm"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              required
            />
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={resettingPassword}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={resettingPassword || !newPassword || !confirmPassword}
            >
              {resettingPassword ? 'Updating…' : 'Reset password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
